import { Box } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { ContextMenuItem, Table, useOnScrollDown } from 'redesign';
import { useTranslation } from 'react-i18next';
import { InfiniteQueryObserverSuccessResult } from 'react-query';
import { GetDocumentsResponse } from 'hq/services/documents-service.types';
import { RenderUnsettledUI } from 'shared/components';
import { useExternalParams } from 'shared/hooks';
import {
  DOCUMENTS_INITIAL_VALUES,
  DocumentsColumnDefinition,
  DocumentsParams,
  DocumentsTableDefinition,
  SetDocumentParams,
} from './documents-table.types';
import { getDocumentsColumnDefinition } from './utils/get-documents-column-definition';
import { useGetDocuments } from './hooks/use-get-documents';
import { getDocumentTableData } from './utils/get-document-table-data';
import { DocumentSignDialog } from '../dialogs/document-sign-dialog/document-sign-dialog';

type DocumentsTableProps = {
  documentsQuery: InfiniteQueryObserverSuccessResult<GetDocumentsResponse, unknown>;
  params: DocumentsParams;
  setParams: SetDocumentParams;
};

export const DocumentsTableProvider = () => {
  const [params, setParams] = useExternalParams(DOCUMENTS_INITIAL_VALUES);
  const documentsQuery = useGetDocuments();

  if (documentsQuery.status !== 'success') {
    return <RenderUnsettledUI data={documentsQuery} />;
  }

  return <DocumentsTable documentsQuery={documentsQuery} params={params} setParams={setParams} />;
};

export const DocumentsTable = ({ documentsQuery, params, setParams }: DocumentsTableProps) => {
  const { t } = useTranslation(['web-common', 'hq-screen-specific']);
  const columns = useMemo(() => getDocumentsColumnDefinition(t), []);
  const { data } = documentsQuery;
  const flatData = useMemo(() => data.pages.flatMap((page) => page.items), [data]);
  const tableData = useMemo(() => getDocumentTableData(flatData), [flatData]);
  const [isOpen, setIsOpen] = useState(false);

  const { fetchIfBottomScrolled } = useOnScrollDown({
    infiniteQueryResult: documentsQuery,
    flatData: flatData,
  });

  const handleOpenDialog = useCallback((documentId: string) => {
    setIsOpen(true);
    setParams({ documentId });
  }, []);

  const handleCloseDialog = useCallback(() => {
    setIsOpen(false);
    setParams({ documentId: undefined });
  }, [isOpen]);

  const getRowActions = (documentId: string) => {
    return [
      <ContextMenuItem key="detail" onClick={() => handleOpenDialog(documentId)}>
        {t('document_check.table.actions.detail', { ns: 'hq-screen-specific' })}
      </ContextMenuItem>,
      <ContextMenuItem key="edit" onClick={() => {}}>
        {t('document_check.table.actions.edit', { ns: 'hq-screen-specific' })}
      </ContextMenuItem>,
    ];
  };
  return (
    <Box height="90%">
      <Table<DocumentsColumnDefinition, DocumentsTableDefinition>
        stickyHeader
        columns={columns}
        data={tableData}
        getRowActions={getRowActions}
        data-testid="documents-virtual-table"
        onScroll={fetchIfBottomScrolled}
      />
      {params.documentId ? (
        <DocumentSignDialog
          isOpen={isOpen}
          onClose={handleCloseDialog}
          documentId={params.documentId}
        />
      ) : null}
    </Box>
  );
};
